/**
 * Conserve la position de défilement du menu latéral d'un écran à l'autre.
 *
 * Le menu est long : cliquer sur une entrée basse chargeait la nouvelle page,
 * Nuxt remettait le défilement à zéro, et le menu remontait avec lui — il
 * fallait redescendre à chaque navigation.
 *
 * Deux contextes, une même mécanique :
 *
 * - Sur bureau, le menu défile pour son propre compte, indépendamment de la
 *   page. Le contenu repart bien en haut à chaque navigation, comme attendu,
 *   tandis que le menu garde à l'écran la zone où l'on vient de cliquer. Sa
 *   barre est masquée (`.menu-defilant`) : il déborde dès que les groupes sont
 *   dépliés, et l'afficher ferait doublon avec celle de la page.
 * - En mobile, c'est le panneau superposé qui défile, et il se rouvre là où on
 *   l'avait laissé plutôt que de repartir du début.
 *
 * Les deux positions sont distinctes : le panneau mobile et la colonne bureau
 * n'ont ni la même hauteur ni le même contenu visible, et partager une seule
 * valeur ferait sauter l'un au gré de l'autre lors d'un changement d'orientation
 * ou de largeur.
 */

/**
 * Dernières positions connues, par contexte, partagées entre les instances.
 *
 * En mémoire de session : la position d'un ascenseur n'est pas une préférence
 * à faire survivre à la fermeture de l'onglet, contrairement aux groupes
 * repliés qui, eux, vont en `localStorage`.
 */
const positions: Record<string, number | undefined> = {}

/**
 * @param cle Contexte de défilement : `desktop` pour la colonne, `mobile` pour
 *            le panneau superposé, `inutilise` pour une instance qui ne défile
 *            pas et ne doit écraser aucune des deux.
 */
export function useSidebarScroll(cle: 'desktop' | 'mobile' | 'inutilise' = 'desktop') {
  /** L'élément défilant, lié via `ref` dans le composant. */
  const el = ref<HTMLElement | null>(null)

  /** Relève la position courante, avant que l'élément ne disparaisse. */
  function remember() {
    if (el.value) positions[cle] = el.value.scrollTop
  }

  /**
   * Repose la position mémorisée.
   *
   * Appelée depuis `onMounted` et à l'ouverture du panneau mobile : celui-ci
   * naît d'un `v-if`, donc son montage ne coïncide pas avec celui du composant
   * qui le porte.
   *
   * Le navigateur écrête la valeur si le menu est plus court dans ce contexte
   * (rôle non administrateur, groupe replié entre-temps) : pas de cas
   * particulier à traiter.
   */
  function restore() {
    const y = positions[cle]

    /*
     * `y != null` et non `y` : une position de zéro est une position valide —
     * l'utilisateur a remonté le menu en haut. La tester par sa véracité la
     * confondrait avec « rien de mémorisé » et figerait la restauration, le
     * panneau mobile réutilisant son conteneur d'une ouverture à l'autre.
     */
    if (el.value && y != null) el.value.scrollTop = y
  }

  /*
   * Posée dans `onMounted`, la restauration s'applique au nœud déjà inséré mais
   * pas encore affiché : aucun saut visible.
   */
  onMounted(restore)

  /*
   * Filet pour les sorties qui ne partent pas d'un clic dans le menu : retour
   * arrière du navigateur, redirection après enregistrement, fermeture du
   * panneau par le backdrop.
   */
  onBeforeUnmount(remember)

  return { el, remember, restore }
}
